import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import Main from "../Main";
import { AppBreadcrumbs } from "../../components/listing";
import StatusBadge from "../../components/listing/StatusBadge";
import { useBranding } from "../../context/BrandingContext";
import { useNotify } from "../../context/NotifyContext";
import blueLogo from "../../assests/newbrand/13.svg";
import {
  buildTeamMemberDocument,
  cacheTeamMember,
  downloadTeamMemberPdf,
  printTeamMember,
  readCachedTeamMember,
} from "../../utils/teamMemberDocument";

const TeamMemberDetailPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { notify } = useNotify();
  const { companyName, legalName, mediaUrl, settings } = useBranding();
  const [member, setMember] = useState(location.state?.member || null);
  const [loading, setLoading] = useState(!location.state?.member);

  const brand = useMemo(() => {
    const logoPath = settings?.logoLightUrl || settings?.logoDarkUrl;
    return {
      companyName,
      legalName,
      logoUrl: logoPath ? mediaUrl(logoPath) : blueLogo,
    };
  }, [companyName, legalName, mediaUrl, settings]);

  useEffect(() => {
    const seed = location.state?.member;
    if (seed) {
      cacheTeamMember(id, seed);
      setMember(seed);
      setLoading(false);
      return;
    }
    const cached = readCachedTeamMember(id);
    if (cached) {
      setMember(cached);
      setLoading(false);
      return;
    }
    setLoading(false);
  }, [id, location.state?.member]);

  const doc = useMemo(
    () => buildTeamMemberDocument(member, brand),
    [member, brand]
  );

  const handlePrint = () => {
    if (!doc) return;
    const ok = printTeamMember(doc);
    if (!ok) notify("Please allow pop-ups to print", "warning");
  };

  const handlePdf = async () => {
    if (!doc) return;
    try {
      await downloadTeamMemberPdf(doc);
      notify("PDF downloaded", "success");
    } catch {
      notify("PDF download failed", "error");
    }
  };

  return (
    <Main showRightPanel={false} showInfoText={false}>
      <div className="mt-2">
        <AppBreadcrumbs
          items={[
            { label: "Dashboard", to: "/" },
            { label: "My Team Members", to: "/team" },
            { label: "Member Details" },
          ]}
        />

        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <button
            type="button"
            className="ds-btn ds-btn-secondary ds-btn-sm"
            onClick={() => navigate("/team")}
          >
            <ArrowBackOutlinedIcon sx={{ fontSize: 16 }} /> Back
          </button>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="ds-btn ds-btn-secondary ds-btn-sm"
              onClick={() => navigate(`/fund/report/${id}`)}
              disabled={!id}
            >
              <HistoryOutlinedIcon sx={{ fontSize: 16 }} /> History
            </button>
            <button
              type="button"
              className="ds-btn ds-btn-secondary ds-btn-sm"
              onClick={handlePrint}
              disabled={!doc}
            >
              <PrintOutlinedIcon sx={{ fontSize: 16 }} /> Print
            </button>
            <button
              type="button"
              className="ds-btn ds-btn-primary ds-btn-sm"
              onClick={handlePdf}
              disabled={!doc}
            >
              <PictureAsPdfOutlinedIcon sx={{ fontSize: 16 }} /> Download PDF
            </button>
          </div>
        </div>

        {loading ? (
          <div className="ds-card text-[13px] text-[var(--text-muted)]">Loading details...</div>
        ) : !doc ? (
          <div className="ds-card text-[13px] text-[var(--text-muted)]">
            Member details not available. Go back to Team and open View again.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="ds-card space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[12px] text-[var(--text-muted)] uppercase tracking-wide">
                    {doc.subtitle}
                  </p>
                  <h1 className="text-[16px] font-semibold text-[var(--text-dark)] mt-0.5">
                    {doc.companyName}
                  </h1>
                  {doc.legalName ? (
                    <p className="text-[11px] text-[var(--text-muted)]">{doc.legalName}</p>
                  ) : null}
                </div>
                {doc.logoUrl ? (
                  <img
                    src={doc.logoUrl}
                    alt={doc.companyName}
                    className="h-10 max-w-[120px] object-contain"
                  />
                ) : null}
              </div>

              <div className="rounded-[3px] border border-[var(--border-color)] bg-[var(--bg-app)] p-3 text-center">
                <p className="text-[11px] text-[var(--text-muted)]">Agent</p>
                <p className="text-[20px] font-bold text-[var(--text-dark)] capitalize">
                  {member?.name || "—"}
                </p>
                <p className="text-[12px] text-[var(--text-muted)] mt-0.5">
                  ID: {member?.userName || "—"}
                </p>
                <div className="mt-2 flex justify-center">
                  <StatusBadge status={doc.status} />
                </div>
              </div>
            </div>

            <div className="ds-card">
              <h2 className="text-[14px] font-semibold text-[var(--text-dark)] mb-3">
                Member Details
              </h2>
              <div className="space-y-0">
                {doc.fields.map((f) => (
                  <div
                    key={f.label}
                    className="flex justify-between gap-3 py-2 border-b border-[var(--border-color)] text-[12px] last:border-0"
                  >
                    <span className="text-[var(--text-muted)] font-medium">{f.label}</span>
                    <span className="text-[var(--text-dark)] font-semibold text-right break-all">
                      {f.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Main>
  );
};

export default TeamMemberDetailPage;
